'use client'

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'

export type ScoutProduct = 'radar' | 'vector' | 'hive'
type Emotion = 'curious' | 'surprised' | 'proud' | 'excited'

/**
 * Scout's pose. `t` seeks the drawing's own homepage tracks (seconds on its
 * 7.4s timeline, 0 is rest), only ever forward through one gesture. The rest
 * are drawn here in viewBox units: pupil gaze, face turn, torso lean about
 * the hips (degrees), torso dip and the presenting arm (0 to 1). Then the
 * whole figure's travel (x, y; the shadow stays on the panel), each leg's
 * swing about its hip (degrees), the presenting arm raised overhead (0 to 1)
 * and its wrist (degrees). The last stage's beat and the return to rest end
 * with those six at 0, so the settled Scout stands where it is served.
 */
type Pose = {
  t: number
  gx: number
  gy: number
  fx: number
  fy: number
  lean: number
  dip: number
  present: number
  x: number
  y: number
  legLeft: number
  legRight: number
  raise: number
  wrist: number
}
/** At `at` ms into a beat Scout reaches `pose` and, if set, `emotion`. */
type Step = { at: number; pose?: Partial<Pose>; emotion?: Emotion | null }
type Beat = {
  keys: [number, Pose][]
  moods: [number, Emotion | null][]
  end: number
}
type Script = {
  /** The existing panel Scout watches, and the attribute its sequence sets. */
  panel: string
  attribute: 'data-in' | 'data-stage'
  /** The stage as the panel publishes it; undefined if it publishes none. */
  read: (panel: HTMLElement) => string | undefined
  /** The panel's resting stage; Scout returns to `rest` when it gets there. */
  idle: string
  /** The panel's last stage; once its beat settles Scout follows the mouse. */
  done: string
  /** Scout before the panel moves. Its gaze is also the static art's. */
  rest: Pose
  /** One short beat for each stage the panel reaches. */
  beats: Record<string, Step[]>
}

const REST: Pose = {
  t: 0,
  gx: 0,
  gy: 0,
  fx: 0,
  fy: 0,
  lean: 0,
  dip: 0,
  present: 0,
  x: 0,
  y: 0,
  legLeft: 0,
  legRight: 0,
  raise: 0,
  wrist: 0,
}

const SCRIPTS: Record<ScoutProduct, Script> = {
  // #reportCard reveals once (data-in) and its bars fill: two steps in while
  // the lens comes up to the eye, examine the sample, lower it with eyes on
  // the priority finding below, then two steps back to its mark.
  radar: {
    panel: 'reportCard',
    attribute: 'data-in',
    read: panel => (panel.hasAttribute('data-in') ? 'in' : 'out'),
    idle: 'out',
    done: 'in',
    rest: REST,
    beats: {
      in: [
        // The tracks hold the rest pose until 0.35s, so start there.
        { at: 0, pose: { t: 0.35 }, emotion: 'curious' },
        { at: 175, pose: { t: 0.7, x: 11, legRight: -12, legLeft: 6 } },
        { at: 350, pose: { t: 1.05, x: 22, legRight: 0, legLeft: 0 } },
        { at: 525, pose: { t: 1.25, x: 33, legLeft: -12, legRight: 6 } },
        { at: 700, pose: { t: 1.45, x: 44, legLeft: 0, legRight: 0 } },
        { at: 850, emotion: 'surprised' },
        { at: 1200, pose: { t: 1.45 } },
        { at: 1550, pose: { t: 1.82 }, emotion: 'curious' },
        { at: 2000, pose: { t: 2.35, gx: 4, gy: 4 } },
        { at: 2200, pose: { x: 33, legLeft: 10, legRight: -5 } },
        { at: 2400, pose: { x: 22, legLeft: 0, legRight: 0 } },
        { at: 2550, pose: { x: 11, legRight: 10, legLeft: -5 } },
        { at: 2700, pose: { x: 0, legRight: 0, legLeft: 0 } },
      ],
    },
  },
  // #lead: at 2 the score counts and the bars fill, at 3 the route is ready.
  // Scout stands at the card's right looking in, watches the score, follows
  // the bars, then crouches, hops, lands and presents the route with a turn
  // of the wrist.
  vector: {
    panel: 'lead',
    attribute: 'data-stage',
    read: panel => panel.dataset.stage,
    idle: '0',
    done: '3',
    rest: { ...REST, gx: -14, gy: 3 },
    beats: {
      '2': [
        { at: 0, emotion: 'curious' },
        { at: 300, pose: { gx: -8, gy: 6, fx: -2, fy: 4, present: 0 } },
        { at: 650, pose: { gx: -8, gy: 6, fx: -2, fy: 4 } },
        { at: 1150, emotion: 'surprised' },
        { at: 1350, pose: { gx: -16, gy: 6, fx: -8, fy: 4, lean: -2 } },
      ],
      '3': [
        { at: 0, emotion: 'proud' },
        { at: 150, pose: { dip: 8 } },
        { at: 300, pose: { y: -44, dip: -2, legLeft: -5, legRight: 5 } },
        { at: 460, pose: { y: 0, dip: 7, legLeft: 0, legRight: 0 } },
        { at: 620, pose: { dip: 0 } },
        { at: 900, pose: { present: 1, lean: -3 } },
        { at: 1100, pose: { wrist: -22 } },
        { at: 1300, pose: { wrist: 14 } },
        { at: 1500, pose: { wrist: 0 } },
        { at: 1900, pose: { present: 1, lean: -3 } },
        { at: 2400, pose: { present: 0, lean: 0, fx: -6, fy: 3 } },
      ],
    },
  },
  // #relay: 1 the request reaches the core, 2 context loads, 3 the first
  // agent takes it, 4 every agent has it. Scout, above the core, follows the
  // handoff left to right with a side-step, hops back to the core when the
  // relay is ready, nods and opens a hand.
  hive: {
    panel: 'relay',
    attribute: 'data-stage',
    read: panel => panel.dataset.stage,
    idle: '0',
    done: '4',
    rest: { ...REST, gx: -8, gy: 4 },
    beats: {
      '1': [
        { at: 0, emotion: 'curious' },
        { at: 350, pose: { gx: -18, gy: 4, fx: -9, fy: 2, lean: -3 } },
      ],
      '2': [{ at: 400, pose: { gx: -8, gy: 7, fx: -2, fy: 5, lean: 0 } }],
      // Stage 4 follows 600ms later and hops Scout back to the core.
      '3': [
        { at: 0, emotion: 'excited' },
        { at: 200, pose: { x: 10, legRight: -10, legLeft: 5 } },
        {
          at: 400,
          pose: { gx: 0, gy: 7, fx: 4, fy: 4, lean: 2, x: 18 },
        },
        { at: 520, pose: { legRight: 0, legLeft: 0 } },
      ],
      '4': [
        { at: 0, emotion: 'proud' },
        {
          at: 160,
          pose: { gx: -8, gy: 6, fx: 0, fy: 9, lean: 0, dip: 8 },
        },
        {
          at: 320,
          pose: { x: 8, y: -36, fy: 2, dip: -2, legLeft: -5, legRight: 5 },
        },
        {
          at: 470,
          pose: { x: 0, y: 0, fy: 9, dip: 7, legLeft: 0, legRight: 0 },
        },
        { at: 650, pose: { fy: 3, dip: 0 } },
        { at: 850, pose: { present: 0.8, raise: 0.4, wrist: -12 } },
        { at: 1050, pose: { wrist: 6 } },
        { at: 1350, pose: { wrist: 6 } },
        { at: 1650, pose: { present: 0, raise: 0, wrist: 0 } },
      ],
    },
  },
}

const mix = (from: number, to: number, progress: number) =>
  from + (to - from) * progress
const ease = (progress: number) => progress * progress * (3 - 2 * progress)
const blend = (from: Pose, to: Pose, progress: number): Pose => ({
  t: mix(from.t, to.t, progress),
  gx: mix(from.gx, to.gx, progress),
  gy: mix(from.gy, to.gy, progress),
  fx: mix(from.fx, to.fx, progress),
  fy: mix(from.fy, to.fy, progress),
  lean: mix(from.lean, to.lean, progress),
  dip: mix(from.dip, to.dip, progress),
  present: mix(from.present, to.present, progress),
  x: mix(from.x, to.x, progress),
  y: mix(from.y, to.y, progress),
  legLeft: mix(from.legLeft, to.legLeft, progress),
  legRight: mix(from.legRight, to.legRight, progress),
  raise: mix(from.raise, to.raise, progress),
  wrist: mix(from.wrist, to.wrist, progress),
})
const clamp = (value: number) => Math.max(-1, Math.min(1, value))
// The wave after the last beat: how long it lasts, and how long before the
// next can start.
const WAVE_MS = 1300
const WAVE_COOLDOWN_MS = 6000
const gazeOf = ({ gx, gy }: Pose) => `translate(${gx}px, ${gy}px)`
// The same predicate RefScript, ShellEffects and HeroSequence use.
const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  document.body.classList.contains('reduce-motion')

/**
 * Moves a product hero's Scout only when its panel's own sequence moves: a
 * MutationObserver on the panel's data-in / data-stage plays one short beat
 * per change, so the panel's Replay restarts Scout with it. No clock of its
 * own and no loop; off screen or in a hidden tab a beat jumps to its end.
 * Reduced motion takes each beat's end pose without moving, and without JS
 * the served drawing stands at rest. Once the last stage's beat settles, a
 * mouse moving in the hero turns Scout's eyes, face and lean toward it, and
 * coming near earns one short wave (at most every 6s); touch never does.
 */
export default function ProductScoutMotion({
  product,
  idPrefix,
  className,
  children,
}: {
  product: ScoutProduct
  idPrefix: string
  className: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stage = ref.current
    const {
      panel: panelId,
      attribute,
      read,
      idle,
      done,
      rest,
      beats,
    } = SCRIPTS[product]
    const panel = stage?.parentElement?.querySelector<HTMLElement>(
      `#${panelId}`
    )
    const svg = stage?.querySelector<SVGSVGElement>('svg.hero-scout')
    // The mouse is followed anywhere in this product's hero, nowhere else.
    const hero = stage?.closest<HTMLElement>('section') ?? stage?.parentElement
    if (!stage || !panel || !svg || !hero) return

    const art = svg.querySelector<SVGGElement>(`#${idPrefix}-scout-art`)
    const shadow = svg.querySelector<SVGEllipseElement>(
      `#${idPrefix}-ground-shadow`
    )
    const legLeft = svg.querySelector<SVGGElement>('[data-scout-leg="left"]')
    const legRight = svg.querySelector<SVGGElement>('[data-scout-leg="right"]')
    const face = svg.querySelector<SVGGElement>(`#${idPrefix}-face`)
    const faceRest = face?.getAttribute('transform') ?? null
    const torso = svg.querySelector<SVGGElement>(`#${idPrefix}-idle-torso`)
    const gaze = Array.from(
      svg.querySelectorAll<SVGGElement>('[data-scout-gaze]')
    )
    const presentArm = svg.querySelector<SVGGElement>(
      '[data-scout-present="arm"]'
    )
    const presentPalm = svg.querySelector<SVGGElement>(
      '[data-scout-present="hand"]'
    )
    const palmRest = presentPalm?.getAttribute('transform') ?? ''
    const tracks = Array.from(
      svg.querySelectorAll<SVGAnimationElement>('animate, animateTransform')
    )
    const seekable =
      typeof svg.pauseAnimations === 'function' &&
      typeof svg.setCurrentTime === 'function'
    // A beat seeks the tracks frame by frame; they never run on their own.
    if (seekable) svg.pauseAnimations()

    // Back to rest inside the gap before the panel's next stage (250ms on
    // Hive's Replay, 300ms on Vector's).
    const toRest: Step[] = [
      { at: 0, emotion: null },
      { at: 240, pose: rest },
    ]
    let pose = rest
    let mood: Emotion | null = null
    let begun = false
    let current: string | undefined = idle
    let beat: (Beat & { start: number }) | null = null
    let frame = 0
    const box = stage.getBoundingClientRect()
    let onScreen = box.bottom > 0 && box.top < window.innerHeight
    // Once the last beat settles: that pose, the mouse in the hero, Scout's
    // eased look toward it, and when the current and the last wave began.
    let home: Pose | null = null
    let aim: Pose | null = null
    let pointer: { x: number; y: number } | null = null
    let wave: number | null = null
    let waved = -Infinity
    let reactFrame = 0

    const seek = (t: number) => {
      if (!seekable) return
      if (!begun) {
        svg.setCurrentTime(0)
        tracks.forEach(track => track.beginElement())
        begun = true
      }
      svg.setCurrentTime(t)
    }
    // The homepage's presenting arm, swung down and in toward the panel,
    // raised overhead to wave, and turned at the wrist about its hand.
    const present = (progress: number, raise: number, wrist: number) => {
      if (progress > 0) {
        if (stage.dataset.scoutGesture !== 'present')
          stage.dataset.scoutGesture = 'present'
        presentArm?.setAttribute(
          'transform',
          `translate(207 370) rotate(${(mix(-72, -25, progress) + raise * 55).toFixed(2)}) scale(${mix(0.75, 1, progress).toFixed(3)}) translate(-207 -370)`
        )
        presentPalm?.setAttribute(
          'transform',
          `translate(100 350) rotate(${(mix(57, -64, progress) + raise * 30 + wrist).toFixed(2)})`
        )
      } else {
        delete stage.dataset.scoutGesture
        presentArm?.removeAttribute('transform')
        presentPalm?.setAttribute('transform', palmRest)
      }
    }
    const feel = (next: Emotion | null) => {
      if (next === mood) return
      mood = next
      if (next) stage.dataset.scoutEmotion = next
      else delete stage.dataset.scoutEmotion
    }
    const apply = (next: Pose) => {
      if (next.t !== pose.t) {
        seek(next.t)
        // The lens shows the magnified eye while it is up at the eye.
        if (next.t >= 1.25 && next.t <= 1.65)
          stage.dataset.scoutLens = 'inspect'
        else delete stage.dataset.scoutLens
      }
      if (next.gx !== pose.gx || next.gy !== pose.gy) {
        const transform = `translate(${next.gx.toFixed(2)}px, ${next.gy.toFixed(2)}px)`
        gaze.forEach(group => {
          group.style.transform = transform
        })
      }
      if (next.fx !== pose.fx || next.fy !== pose.fy)
        face?.setAttribute(
          'transform',
          `translate(${next.fx.toFixed(2)} ${next.fy.toFixed(2)})`
        )
      if (next.lean !== pose.lean || next.dip !== pose.dip)
        torso?.setAttribute(
          'transform',
          `translate(0 ${next.dip.toFixed(2)}) rotate(${next.lean.toFixed(2)} 320 458)`
        )
      if (
        next.present !== pose.present ||
        next.raise !== pose.raise ||
        next.wrist !== pose.wrist
      )
        present(next.present, next.raise, next.wrist)
      if (next.x !== pose.x || next.y !== pose.y) {
        if (next.x || next.y)
          art?.setAttribute(
            'transform',
            `translate(${next.x.toFixed(2)} ${next.y.toFixed(2)})`
          )
        else art?.removeAttribute('transform')
        // The shadow stays on the panel, and tightens while Scout is up.
        if (next.y)
          shadow?.setAttribute(
            'transform',
            `translate(322 ${(556 - next.y).toFixed(2)}) scale(${(1 + Math.min(0, next.y) / 240).toFixed(3)}) translate(-322 -556)`
          )
        else shadow?.removeAttribute('transform')
      }
      // Each leg swings about its hip, carrying its boot.
      if (next.legLeft !== pose.legLeft) {
        if (next.legLeft)
          legLeft?.setAttribute(
            'transform',
            `rotate(${next.legLeft.toFixed(2)} 272 449)`
          )
        else legLeft?.removeAttribute('transform')
      }
      if (next.legRight !== pose.legRight) {
        if (next.legRight)
          legRight?.setAttribute(
            'transform',
            `rotate(${next.legRight.toFixed(2)} 365 451)`
          )
        else legRight?.removeAttribute('transform')
      }
      pose = next
    }
    // Keyframes run from wherever Scout is now, so a beat that interrupts
    // another (Replay, or the next stage) carries on without a jump.
    const resolve = (steps: Step[]): Beat => {
      let last = pose
      const keys: [number, Pose][] = [[0, last]]
      const moods: [number, Emotion | null][] = []
      steps.forEach(({ at, pose: change, emotion }) => {
        if (change) {
          last = { ...last, ...change }
          // A step at 0 only swaps in an identical pose (Radar's 0.35s).
          if (at === 0) keys[0] = [0, last]
          else keys.push([at, last])
        }
        if (emotion !== undefined) moods.push([at, emotion])
      })
      const end = Math.max(
        ...keys.map(([at]) => at),
        ...moods.map(([at]) => at)
      )
      return { keys, moods, end }
    }
    const settle = ({ keys, moods }: Beat) => {
      apply(keys[keys.length - 1][1])
      if (moods.length) feel(moods[moods.length - 1][1])
    }
    const tick = (now: number) => {
      frame = 0
      if (!beat) return
      const elapsed = Math.max(0, now - beat.start)
      const { keys, moods } = beat
      const next = keys.findIndex(([at]) => at >= elapsed)
      if (next < 0) apply(keys[keys.length - 1][1])
      else if (next === 0) apply(keys[0][1])
      else {
        const [fromAt, from] = keys[next - 1]
        const [toAt, to] = keys[next]
        apply(blend(from, to, ease((elapsed - fromAt) / (toAt - fromAt))))
      }
      const felt = moods.filter(([at]) => at <= elapsed).pop()
      if (felt) feel(felt[1])
      if (elapsed < beat.end) frame = requestAnimationFrame(tick)
      else {
        beat = null
        rested()
      }
    }
    const finish = () => {
      if (!beat) return
      cancelAnimationFrame(frame)
      frame = 0
      const ended = beat
      beat = null
      settle(ended)
      rested()
    }
    // The mouse only moves Scout once the last stage's beat has settled.
    const rested = () => {
      home = current === done ? pose : null
      aim = home
    }
    // One frame of following the mouse: ease the look toward it (or back to
    // the completed pose once it leaves) and play any wave on top. Frames
    // run only until both have settled; nothing loops on its own.
    const react = (now: number) => {
      reactFrame = 0
      const from = aim
      if (!home || !from) return
      let look = home
      if (pointer) {
        // In Scout-widths from its eyes (about 335, 290 of the viewBox).
        const rect = stage.getBoundingClientRect()
        const dx = (pointer.x - rect.left) / rect.width - 0.52
        const dy = (pointer.y - rect.top) / rect.height - 0.45
        const nx = clamp(dx / 2)
        const ny = clamp(dy / 2)
        look = {
          ...home,
          gx: nx * 16,
          gy: 2 + ny * 7,
          fx: nx * 7,
          fy: 2 + ny * 4,
          lean: nx * 2.5,
        }
        if (Math.hypot(dx, dy) < 0.75 && now - waved > WAVE_COOLDOWN_MS)
          wave = waved = now
      }
      const gap = Math.max(
        ...(['gx', 'gy', 'fx', 'fy', 'lean'] as const).map(key =>
          Math.abs(look[key] - from[key])
        )
      )
      aim = gap < 0.05 ? look : blend(from, look, 0.16)
      let next = aim
      if (wave !== null) {
        const elapsed = now - wave
        if (elapsed < WAVE_MS) {
          // Up over 220ms, three swings of the hand, down over the last 260.
          const lift = ease(
            Math.min(1, elapsed / 220, (WAVE_MS - elapsed) / 260)
          )
          next = {
            ...aim,
            present: mix(home.present, 1, lift),
            raise: lift,
            wrist: lift * 24 * Math.sin((elapsed / WAVE_MS) * Math.PI * 6),
          }
        } else wave = null
      }
      apply(next)
      if (wave !== null || aim !== look)
        reactFrame = requestAnimationFrame(react)
    }
    // Stops following and stands Scout in its completed pose, without a
    // transition; only a fresh mouse move starts it again.
    const calm = () => {
      cancelAnimationFrame(reactFrame)
      reactFrame = 0
      pointer = null
      wave = null
      if (home) {
        aim = home
        apply(home)
      }
    }
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || !home) return
      if (!onScreen || document.hidden || reducedMotion()) return
      pointer = { x: event.clientX, y: event.clientY }
      if (!reactFrame) reactFrame = requestAnimationFrame(react)
    }
    const onPointerLeave = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || !pointer) return
      pointer = null
      if (!reactFrame) reactFrame = requestAnimationFrame(react)
    }
    const follow = () => {
      const next = read(panel)
      // Both reveal paths set data-in, and Replay resets a resting panel.
      if (next === current) return
      current = next
      // A new stage (Replay, too) ends any following where it is; the beat
      // below carries on from there.
      cancelAnimationFrame(reactFrame)
      reactFrame = 0
      pointer = null
      wave = null
      home = aim = null
      // No stage is not the resting stage: Scout plays nothing until the
      // panel publishes one (both serve data-stage='0').
      if (next === undefined) return
      const steps = next === idle ? toRest : beats[next]
      if (!steps) return
      cancelAnimationFrame(frame)
      frame = 0
      beat = null
      const planned = resolve(steps)
      if (reducedMotion() || !onScreen || document.hidden) {
        settle(planned)
        rested()
      } else {
        beat = { ...planned, start: performance.now() }
        frame = requestAnimationFrame(tick)
      }
    }

    const watch = new MutationObserver(follow)
    watch.observe(panel, { attributes: true, attributeFilter: [attribute] })
    const view = new IntersectionObserver(entries => {
      onScreen = entries[entries.length - 1].isIntersecting
      if (!onScreen) {
        finish()
        calm()
      }
    })
    view.observe(stage)
    const onVisibility = () => {
      if (document.hidden) {
        finish()
        calm()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    // Reduced motion turned on mid-beat ends the beat at its last pose. Like
    // HeroSequence, watch both the media query and body.reduce-motion.
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onMotion = () => {
      if (reducedMotion()) {
        finish()
        calm()
      }
    }
    motion.addEventListener('change', onMotion)
    const bodyWatch = new MutationObserver(onMotion)
    bodyWatch.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
    // Passive and decorative: nothing here stops or redirects the pointer.
    hero.addEventListener('pointermove', onPointerMove, { passive: true })
    hero.addEventListener('pointerleave', onPointerLeave)
    // The panel may have moved before this mounted (Radar's reveal often has).
    follow()

    return () => {
      cancelAnimationFrame(frame)
      cancelAnimationFrame(reactFrame)
      beat = null
      watch.disconnect()
      view.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      motion.removeEventListener('change', onMotion)
      bodyWatch.disconnect()
      hero.removeEventListener('pointermove', onPointerMove)
      hero.removeEventListener('pointerleave', onPointerLeave)
      // Hand the drawing back as served: a Strict Mode remount reuses it.
      feel(null)
      delete stage.dataset.scoutLens
      present(0, 0, 0)
      gaze.forEach(group => group.style.removeProperty('transform'))
      if (faceRest !== null) face?.setAttribute('transform', faceRest)
      torso?.removeAttribute('transform')
      art?.removeAttribute('transform')
      shadow?.removeAttribute('transform')
      legLeft?.removeAttribute('transform')
      legRight?.removeAttribute('transform')
      if (begun) svg.setCurrentTime(0)
    }
  }, [product, idPrefix])

  // Served with the rest gaze, so the static art already looks the right way.
  const restGaze = {
    '--scout-gaze': gazeOf(SCRIPTS[product].rest),
  } as CSSProperties
  return (
    <div ref={ref} className={className} style={restGaze} aria-hidden='true'>
      {children}
    </div>
  )
}
